import type { SelectionEntry } from 'Models/index';
import { type ExternalPageSize, normalizePageSize, type PageSizeMode } from 'Models/pagination';
import type {
  NormalizedSearchRequest,
  ParsedSearchRequest,
  RequestExtra,
  RequestExtraValue,
  SearchRequestDefaults,
} from 'Models/uiConfig/requestTypes';
import {
  CORRECTION_LEVEL_FLAGS,
  type CorrectionLevelFlag,
  type CorrectionLevelInput,
  VariantsMode,
} from 'Modules/serverApi/types';
import { isObjectRecord, type ObjectRecord } from 'Utils/typeGuards';

// All keys of `SearchRequestDefaults` whose value type extends `string | undefined`.
// Derived (rather than restated) so adding a new string-typed default in
// `SearchRequestDefaults` automatically opts into the `getString` normalization.
type StringRequestKey = {
  [K in keyof SearchRequestDefaults]-?: SearchRequestDefaults[K] extends string | undefined ? K : never;
}[keyof SearchRequestDefaults];

function isPlainObject(value: unknown): value is ObjectRecord {
  if (!isObjectRecord(value)) {
    return false;
  }

  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function getString(
  parsed: ParsedSearchRequest,
  defaults: SearchRequestDefaults,
  key: StringRequestKey,
): string | undefined {
  const parsedValue = parsed[key];
  const defaultValue = defaults[key];

  return typeof parsedValue === 'string'
    ? parsedValue
    : typeof defaultValue === 'string'
      ? defaultValue
      : undefined;
}

function isRequestExtraValue(value: unknown): value is RequestExtraValue {
  if (!value || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return true;
  }

  if (Array.isArray(value)) {
    return value.every(isRequestExtraValue);
  }

  return isRequestExtra(value);
}

function isRequestExtra(value: unknown): value is RequestExtra {
  return isPlainObject(value) && Object.values(value).every(isRequestExtraValue);
}

function getExtra(parsed: ParsedSearchRequest, defaults: SearchRequestDefaults): RequestExtra | undefined {
  if (isRequestExtra(parsed.extra)) {
    return parsed.extra;
  }

  return defaults.extra;
}

function isSelectionEntry(value: unknown): value is SelectionEntry {
  return isObjectRecord(value) && typeof value.field === 'string' && typeof value.term === 'string';
}

function getSelection(parsed: ParsedSearchRequest, defaults: SearchRequestDefaults): SelectionEntry[] {
  return Array.isArray(parsed.selection) && parsed.selection.every(isSelectionEntry)
    ? parsed.selection
    : (defaults.selection ?? []);
}

function getPageNumber(parsed: ParsedSearchRequest, defaults: SearchRequestDefaults): number {
  return normalizePageNumber(parsed.pageNumber) ?? normalizePageNumber(defaults.pageNumber) ?? 0;
}

function normalizePageNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
}

function getPageSizeMode(value: unknown): PageSizeMode | undefined {
  return value === 'all' || value === null ? value : undefined;
}

function getExternalPageSize(value: unknown): ExternalPageSize | undefined {
  if (value === 'all') {
    return value;
  }

  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
}

function isCorrectionLevelFlag(value: unknown): value is CorrectionLevelFlag {
  return typeof value === 'string' && (CORRECTION_LEVEL_FLAGS as readonly string[]).includes(value);
}

function asCorrectionLevelInput(value: unknown): CorrectionLevelInput | undefined {
  if (isCorrectionLevelFlag(value)) {
    return value;
  }
  if (Array.isArray(value) && value.every(isCorrectionLevelFlag)) {
    return value;
  }
  return undefined;
}

function getCorrectionLevel(
  parsed: ParsedSearchRequest,
  defaults: SearchRequestDefaults,
): CorrectionLevelInput | undefined {
  return asCorrectionLevelInput(parsed.queryCorrectionLevel) ?? defaults.queryCorrectionLevel;
}

const VARIANTS_MODES = Object.values(VariantsMode) as readonly VariantsMode[];

function asVariantsMode(value: unknown): VariantsMode | undefined {
  return typeof value === 'string' && (VARIANTS_MODES as readonly string[]).includes(value)
    ? (value as VariantsMode)
    : undefined;
}

function getVariantsMode(
  parsed: ParsedSearchRequest,
  defaults: SearchRequestDefaults,
): VariantsMode | undefined {
  return asVariantsMode(parsed.variantsMode) ?? defaults.variantsMode;
}

function getPagination(parsed: ParsedSearchRequest, defaults: SearchRequestDefaults) {
  const parsedPageSizeMode = getPageSizeMode(parsed.pageSizeMode);
  if (parsedPageSizeMode === 'all') {
    return normalizePageSize('all');
  }

  const parsedPageSize = getExternalPageSize(parsed.pageSize);
  if (parsedPageSize !== undefined) {
    return normalizePageSize(parsedPageSize);
  }

  const defaultPageSizeMode = getPageSizeMode(defaults.pageSizeMode);
  if (defaultPageSizeMode === 'all') {
    return normalizePageSize('all');
  }

  return normalizePageSize(getExternalPageSize(defaults.pageSize));
}

export function normalizeParsedSearchRequest(
  parsed: ParsedSearchRequest | null | undefined,
  defaults: SearchRequestDefaults = {},
): NormalizedSearchRequest {
  const parsedRequest = parsed ?? {};
  const pagination = getPagination(parsedRequest, defaults);

  return {
    action: getString(parsedRequest, defaults, 'action'),
    catalog: getString(parsedRequest, defaults, 'catalog') ?? '',
    defaultSearch: getString(parsedRequest, defaults, 'defaultSearch'),
    extra: getExtra(parsedRequest, defaults),
    filterQuery: getString(parsedRequest, defaults, 'filterQuery') ?? '',
    hiddenQuery: getString(parsedRequest, defaults, 'hiddenQuery'),
    mode: getString(parsedRequest, defaults, 'mode'),
    pageNumber: getPageNumber(parsedRequest, defaults),
    pageSize: pagination.pageSize,
    pageSizeMode: pagination.pageSizeMode,
    query: getString(parsedRequest, defaults, 'query') ?? '',
    queryCorrectionLevel: getCorrectionLevel(parsedRequest, defaults),
    referrer: getString(parsedRequest, defaults, 'referrer'),
    selection: getSelection(parsedRequest, defaults),
    sort: getString(parsedRequest, defaults, 'sort') ?? '',
    variantsMode: getVariantsMode(parsedRequest, defaults),
  };
}
